import { useEffect, useRef, useState, type RefObject } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import {
	decideRightPaneAction,
	shouldCollapseEmptyPane,
	type RightPaneSignals,
} from "@/lib/right-pane-visibility";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";

function signalsOf(state: ReturnType<typeof useChatStore.getState>): RightPaneSignals {
	return {
		openFileCount: state.openFileIds.length,
		fileOpenRequestVersion: state.fileOpenRequestVersion,
	};
}

function shouldCollapseEmpty(layout: ReturnType<typeof useLayoutStore.getState>): boolean {
	return shouldCollapseEmptyPane({
		openFileCount: useChatStore.getState().openFileIds.length,
		restorePending: layout.fileTabRestorePending,
		pinned: layout.rightPanePinned,
	});
}

/**
 * 右ペイン (エディタ) を開いているファイルに合わせて開閉する。ファイルがなくなると閉じ、
 * ファイルを開くと閉じる前の幅で開く。ドラッグでの開閉と、ピン留めの設定も尊重する。
 *
 * Returns:
 *   Panel に渡す初期状態と、ドラッグでの開閉を記録するコールバック。
 */
export function useRightPaneVisibility(panelRef: RefObject<ImperativePanelHandle | null>) {
	// リロード直後は URL のタブを復元し終えるまで開いておき、復元時に幅が跳ねないようにする。
	const [initiallyCollapsed] = useState(() => shouldCollapseEmpty(useLayoutStore.getState()));
	const userCollapsedRef = useRef(false);

	useEffect(
		() =>
			useChatStore.subscribe((state, prev) => {
				const panel = panelRef.current;
				if (!panel) return;
				const action = decideRightPaneAction(signalsOf(prev), signalsOf(state), {
					userCollapsed: userCollapsedRef.current,
					pinned: useLayoutStore.getState().rightPanePinned,
				});
				if (action === "collapse") panel.collapse();
				else if (action === "expand" && panel.isCollapsed()) {
					panel.resize(useLayoutStore.getState().centerRightSizes[1]);
				}
			}),
		[panelRef],
	);

	// URL のタブを 1 件も復元できなかったとき・ピン留めを解除したときに、ファイルがなければ閉じる。
	useEffect(
		() =>
			useLayoutStore.subscribe((state, prev) => {
				if (!shouldCollapseEmpty(prev) && shouldCollapseEmpty(state)) panelRef.current?.collapse();
			}),
		[panelRef],
	);

	return {
		initiallyCollapsed,
		// 自動で閉じるのはファイルが 0 件のときだけなので、ファイルがある間の折りたたみはドラッグによる。
		onCollapse: () => {
			if (useChatStore.getState().openFileIds.length > 0) userCollapsedRef.current = true;
		},
		onExpand: () => {
			userCollapsedRef.current = false;
		},
	};
}
